// The shell around `signals.ts`: watch the route, the clock, and the page's
// visibility, and tell the server when the local picture says a tier may have
// been crossed.
//
// MOUNTED ONCE, ABOVE THE ROUTER'S CONTENT. It observes navigation and nothing
// else — it renders nothing, reads no Convex query, and never blocks. A failure
// here must cost an analytics signal and never a page.
//
// WHY THREE TRIGGERS AND NOT ONE. The signal this hook reports is mostly MADE
// between navigations — finishing a chapter takes minutes of scrolling and not
// a single route change — and the ad-funnel visitor this pipeline exists for
// often navigates exactly once: land on a chapter, read, close the tab. A hook
// that only asked at route changes asked such a visitor one question, at the
// moment they had read nothing, and never spoke again; on the first campaign
// days that silenced 7 of the 11 readers who finished a chapter. So:
//
//  1. ROUTE CHANGES — new evidence of exploration, immediate.
//  2. `visibilitychange → hidden` — the one event a single-page session is
//     guaranteed to end with (the reading telemetry flushes on the same one),
//     asked on the short `HIDDEN_RETRY_MS` clock.
//  3. A COARSE TIMER — the reader who finishes and then sits, caught on the
//     ordinary `RETRY_UNCONFIRMED_MS` clock instead of never.
//
// THE HIDDEN LISTENER IS ON `window`, DELIBERATELY. `visibilitychange` fires
// at `document` and bubbles; document listeners run before window listeners
// for the same event. `use-reading-telemetry.ts` flushes its final block
// counts from a DOCUMENT listener, and both its flush and this hook's
// `recordSignals` travel the same Convex client, which delivers a client's
// mutations in submission order — so listening on `window` is what guarantees
// the server verifies AGAINST the final flush rather than against the state
// one flush earlier, exactly when the last blocks of a finished chapter are
// the ones in flight.
//
// WHY THIS IS NOT AN EFFECT-FREE DESIGN. Route changes, the clock, and
// visibility are genuinely external events, and the report is a side effect
// fired in response to them. That is exactly what an effect is for. What is
// deliberately NOT in an effect: the tier arithmetic, which is derived
// synchronously from stored state in `signals.ts`.

import { useEffect, useRef } from "react";
import { useLocation } from "react-router";
import { useConvex } from "convex/react";
import { api } from "@bindery/convex/_generated/api";
import { getPref, setPref, PREF_KEYS } from "@/lib/prefs";
import { getSessionId } from "@/logging";
import { useLoggerContext } from "@/logging";
import { checkoutAttribution } from "@/lib/attribution";
import {
  emptyEngagement,
  hasExplored,
  localCandidateTier,
  shouldReport,
  withAsked,
  withRoute,
  withServerTier,
  HIDDEN_RETRY_MS,
  REPORT_TICK_MS,
} from "./signals";

function readState(): ReturnType<typeof emptyEngagement> {
  return getPref(PREF_KEYS.engagement, emptyEngagement());
}

/**
 * Watch navigation and report engagement tiers.
 *
 * `signedIn` is passed rather than read here so this file has no opinion about
 * how auth is wired. It is a real dependency of the reporting effect, so the
 * account tier fires the moment the session is confirmed — the auth ack lands
 * asynchronously, and hiding it in the ref made the $1.90 signal wait for
 * whatever navigation happened to come next.
 */
export function useEngagementSignals(signedIn: boolean): void {
  const convex = useConvex();
  const { pathname } = useLocation();
  const { leadId } = useLoggerContext();

  // Everything the report needs but must NOT re-fire it, held in a ref: a
  // re-render that changed the Convex client identity, re-resolved the lead
  // id, or confirmed the session would otherwise re-run an effect for a
  // moment already considered. `signedIn` IS still a dependency of the route
  // effect below — the account tier must fire on the auth ack — but the timer
  // and hide paths read it from here.
  const latest = useRef({ convex, leadId, pathname, signedIn });
  useEffect(() => {
    latest.current = { convex, leadId, pathname, signedIn };
  });

  // One doorway for all three triggers, reading everything through the ref so
  // its identity never changes and the mount effect below can close over it.
  // `pathname` is NOT applied here — the route effect applies it before
  // calling, and the other two triggers have no new route to apply.
  const maybeReportRef = useRef((retryMs?: number): void => {
    const {
      leadId: lead,
      convex: client,
      pathname: path,
      signedIn: authed,
    } = latest.current;

    // The founder's and admins' own browsing is not engagement: these rows are
    // the calibration cohort ("does this behaviour predict buying?"), and
    // self-traffic answering that question pollutes it.
    if (path.startsWith("/admin")) return;

    // A lead id is the row's identity. Without one there is nothing to key a
    // visitor on, which happens only when attribution storage is blocked.
    if (lead === undefined || lead === "") return;

    const state = readState();
    const now = Date.now();
    const candidate = localCandidateTier(state, authed);
    if (!shouldReport(state, candidate, now, retryMs)) return;

    // Written BEFORE the call: `withAsked` records the candidate as asked, so
    // a reply that never arrives — or StrictMode's second mount — cannot fire
    // the same question again. The tier itself is only advanced when the
    // server says so.
    setPref(PREF_KEYS.engagement, withAsked(state, candidate, now));

    void client
      .mutation(api.engagement.recordSignals, {
        leadId: lead,
        sessionKey: getSessionId(),
        explored: hasExplored(state),
        // Reuses the checkout path's own resolution, so the click id sent here
        // is the same one a purchase would send — including its staleness
        // eviction, so an expired click is not revived by this call.
        attribution: checkoutAttribution(lead),
      })
      .then((tier: number) => {
        setPref(
          PREF_KEYS.engagement,
          withServerTier(readState(), tier, Date.now()),
        );
      })
      // Analytics must never surface to the reader, and this sits above the
      // router: a rejection here would otherwise be an unhandled one.
      .catch(() => undefined);
  });

  // Trigger 1: navigation. Applies the route to the stored blob first — the
  // route count is `explored`'s evidence — then asks on the default clock.
  useEffect(() => {
    if (pathname.startsWith("/admin")) return;

    const before = readState();
    const state = withRoute(before, pathname);
    if (state !== before) setPref(PREF_KEYS.engagement, state);

    maybeReportRef.current();
  }, [pathname, signedIn]);

  // Triggers 2 and 3: the page hiding, and the open-tab clock. Mounted once —
  // everything varying is read through the ref — so the listener and timer
  // survive navigation instead of being torn down by it.
  useEffect(() => {
    const onVisibility = (): void => {
      if (document.visibilityState === "hidden") {
        maybeReportRef.current(HIDDEN_RETRY_MS);
      }
    };
    // On `window`, never `document` — see the header comment for why the
    // reading telemetry's own flush must win the ordering race.
    window.addEventListener("visibilitychange", onVisibility);
    const tick = setInterval(() => maybeReportRef.current(), REPORT_TICK_MS);
    return () => {
      window.removeEventListener("visibilitychange", onVisibility);
      clearInterval(tick);
    };
  }, []);
}
